import clsx from 'clsx';
import React, { RefObject } from 'react';

import { Icon } from '../icon/Icon';
import { Tab } from '../tab-lines/TabLines';
import styles from './TabPanel.module.scss';

export interface TabPanelProps<T> {
  tabs: Tab<T>[];
  activeTab: T;
  isDisabled?: boolean;
  className?: string;
  onChange: (index: number, tabKey: T) => void;
  tabListRef?: RefObject<HTMLDivElement | null>;
}

export const TabPanel = <T extends string>({
  tabs,
  activeTab,
  onChange,
  tabListRef,
  isDisabled = false,
  className,
  ...rest
}: TabPanelProps<T>) => {
  const currentPanel = tabs.find(i => i.tabKey === activeTab)?.renderPanel;

  return (
    <section
      className={clsx(styles.tabContainer, className)}
      data-testid="tab-panel"
      {...rest}
    >
      <div
        className={styles.tablist}
        role="tablist"
        ref={tabListRef}
        data-tablist
      >
        {tabs.map(({ tabKey, tabLabel, iconName, imgNode }, index) => (
          <button
            type="button"
            key={tabKey}
            className={clsx(styles.tab, {
              [styles.tabActive]: activeTab === tabKey,
              [styles.tabDisabled]: isDisabled,
            })}
            data-testid={`tab-panel-${tabKey}`}
            role="tab"
            aria-selected={activeTab === tabKey}
            onClick={() => {
              if (!isDisabled) {
                onChange(index, tabKey);
              }
            }}
            data-tab-panel-tab
            disabled={isDisabled}
          >
            {imgNode && <div className={styles.imageWrapper}>{imgNode}</div>}
            {iconName && <Icon size={20} iconName={iconName} />}
            <span className={styles.tabLabel}>{tabLabel}</span>
          </button>
        ))}
      </div>

      <div className={styles.tabPanel} data-tab-panel-content>
        {currentPanel}
      </div>
    </section>
  );
};
